import { useWindowSize } from "@react-hookz/web";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Drawer, Icon, Modal, Tooltip, Typography } from "@/components/atoms";
import { useAppSelector } from "@/redux/hooks";
import { useGetAccessListQuery } from "@/redux/apis/assistants/assistantsApi";
import { configSelector } from "@/redux/slices/config/selectors";
import { ClientAccessList, Selection } from "./myClients/ClientAccessList";
import { ClientDetailPanel } from "./myClients/ClientDetailPanel";
import { EmptyStateGraphic } from "./myClients/EmptyStateGraphic";
import { PendingDetailPanel } from "./myClients/PendingDetailPanel";
import { RemoveMyAccessModal } from "./myClients/RemoveMyAccessModal";
import { RequestAccessModal } from "./myClients/RequestAccessModal";

interface MyClientsProps {
  /**
   * Operator-managed EA account (the `:clientId` from the Manage-Client route). Threaded into the
   * access queries so the list reflects that EA's managed clients rather than the logged-in account.
   * Undefined in the EA's own portal (self-service), where queries target the logged-in account.
   */
  accountId?: string;
  /**
   * Read-only view. Defaults to false (the EA's own portal — full access). Set true in the operator's
   * "EA's Clients" tab: the operator may only VIEW the EA's clients, so every mutation affordance is
   * hidden — Request Client Access, Remove My Access, and pending Cancel/Resend. (Removing an EA from
   * a client is done from the client's own Settings → Assistants, not here.)
   */
  readOnly?: boolean;
  /** Section heading. Defaults to "My Clients" (EA portal); the operator tab passes "EA's Clients". */
  title?: string;
}

/**
 * "My Clients" two-panel view. Left panel lists connected + pending client access; the
 * right panel shows the selected client's profile summary, stats, and recent activity.
 *
 * Reused in two contexts:
 * - EA's own portal (self-service): no props — queries target the logged-in EA; EA-gating for the
 *   nav entry lives in ClientNavLinks.
 * - Operator Manage-Client "EA's Clients" tab: `accountId` scopes the list to the managed EA and
 *   `readOnly` hides every mutation affordance (view-only).
 */
export const MyClients = ({ accountId, readOnly = false, title = "My Clients" }: MyClientsProps = {}) => {
  const navigate = useNavigate();
  const { width } = useWindowSize();
  // Operator/brand display name for the heading tooltip; trailing dots stripped to match the
  // "your operator" fallback style used across the client portal (see PendingAssistantRequests).
  const operatorName = (useAppSelector(configSelector)?.name || "your operator").replace(/\.+$/, "");
  // Three responsive layouts keyed off the same Tailwind breakpoints (md=768, lg=1024):
  //  - desktop (lg+)  : two-panel with an inline side detail (unchanged).
  //  - tablet (md–lg) : full-width 2-col card grid; detail opens in a centered Modal.
  //  - mobile (<md)   : full-width card list + sticky Request button; detail opens in a Drawer.
  const isDesktop = width >= 1024;
  const isTablet = width >= 768 && width < 1024;
  const isMobile = width < 768;

  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Selection | null>(null);
  const [requestOpen, setRequestOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);

  // Fetch connected + pending once (unfiltered). Search is applied client-side below so typing never
  // refetches — a debounced `search` param would re-run the query and blank the list on every keystroke.
  const { data: clients = [], isLoading: clientsLoading } = useGetAccessListQuery({
    type: "YOU_MANAGE",
    status: "CONNECTED",
    accountId,
  });
  const { data: pending = [] } = useGetAccessListQuery({ type: "YOU_MANAGE", status: "PENDING", accountId });

  const query = search.trim().toLowerCase();
  const filteredClients = useMemo(
    () =>
      clients.filter((link) => !query || link.party.name?.toLowerCase().includes(query) || link.party.email?.toLowerCase().includes(query)),
    [clients, query]
  );
  const filteredPending = useMemo(
    () =>
      pending.filter((link) => !query || link.party.name?.toLowerCase().includes(query) || link.party.email?.toLowerCase().includes(query)),
    [pending, query]
  );

  // No auto-selection: the landing state shows the "Select a client" placeholder until the EA picks one.

  // Drop any selection when shrinking to tablet/mobile so the Modal/Drawer doesn't pop open for a
  // client the user never tapped.
  useEffect(() => {
    if (!isDesktop) setSelected(null);
  }, [isDesktop]);

  // Resolve against the FILTERED lists so a selection that a search filters out clears the detail
  // panel instead of showing a client that's no longer in the visible list.
  const selectedClient = useMemo(
    () => (selected?.kind === "client" ? filteredClients.find((c) => c.id === selected.id) ?? null : null),
    [selected, filteredClients]
  );
  const selectedPending = useMemo(
    () => (selected?.kind === "pending" ? filteredPending.find((p) => p.id === selected.id) ?? null : null),
    [selected, filteredPending]
  );

  // The EA truly manages nobody — no connected clients, no pending requests, not mid-search. The
  // two-panel layout still renders; this only drives the centered empty state in the right detail
  // panel. A no-match search keeps the "Select a client…" placeholder so the search box stays usable.
  const showEmptyState = !clientsLoading && !search && clients.length === 0 && pending.length === 0;

  const detailContent = selectedClient ? (
    <ClientDetailPanel
      link={selectedClient}
      canRemoveAccess={!readOnly}
      onRemoveAccess={() => setRemoveOpen(true)}
      onManageClient={readOnly ? undefined : () => navigate(`/client/my-clients/${selectedClient.id}/manage`)}
    />
  ) : showEmptyState ? (
    <div className="flex h-full flex-col items-center justify-center gap-2 py-16 text-center">
      <EmptyStateGraphic />
      <Typography variant="action" className="mt-4 font-semibold text-neutral-black">
        No Clients to Manage
      </Typography>
      <Typography variant="paragraph" className="max-w-[250px] text-neutral-dark-gray">
        You are not currently managing any clients. Assigned clients will appear here.
      </Typography>
    </div>
  ) : selectedPending ? (
    <PendingDetailPanel link={selectedPending} onCancelled={() => setSelected(null)} readOnly={readOnly} />
  ) : (
    <div className="flex h-full flex-col items-center justify-center gap-2 py-16 text-center">
      <EmptyStateGraphic />
      <Typography variant="action" className="mt-4 font-semibold text-neutral-black">
        Select a client to get started
      </Typography>
      <Typography variant="paragraph" className="max-w-[250px] text-neutral-dark-gray">
        Select a client from the list to see their full details.
      </Typography>
    </div>
  );

  // Detail content for the tablet Modal / mobile Drawer. The panels move their action buttons from
  // the header (desktop) to a footer (overlay) via the `variant` prop; `readOnly` is threaded through
  // unchanged so the operator's EA view still shows Close-only. Nothing renders with no selection.
  const renderOverlayDetail = (overlayVariant: "modal" | "drawer") => {
    if (selectedClient) {
      return (
        <ClientDetailPanel
          variant={overlayVariant}
          link={selectedClient}
          canRemoveAccess={!readOnly}
          onRemoveAccess={() => setRemoveOpen(true)}
          onManageClient={readOnly ? undefined : () => navigate(`/client/my-clients/${selectedClient.id}/manage`)}
          onClose={() => setSelected(null)}
        />
      );
    }
    if (selectedPending) {
      return (
        <PendingDetailPanel
          variant={overlayVariant}
          link={selectedPending}
          onCancelled={() => setSelected(null)}
          readOnly={readOnly}
          onClose={() => setSelected(null)}
        />
      );
    }
    return null;
  };

  return (
    // Cap + centre so the view doesn't stretch full-width on ultrawide. 1164px matches the Figma
    // frame at 1440 and 2560; a no-op below that width (tablet/mobile unchanged).
    <div className="mx-auto flex min-h-0 w-full max-w-[1164px] flex-1 flex-col px-5 py-6 md:px-8">
      <div className="flex items-center gap-2">
        <Typography variant="h2">{title}</Typography>
        <Tooltip
          content={`A client is a traveller or executive whose trips you manage in ${operatorName}. Add them here so you can book, track, and manage rides on their behalf.`}
        >
          <Icon name="InfoCircle" size="sm" className="shrink-0 text-neutral-dark-gray" />
        </Tooltip>
      </div>

      {isDesktop ? (
        /* ===== Desktop (lg+) — two-panel with inline side detail. The empty case is handled
            inside the right detail panel so the left list stays reachable. ===== */
        <div className="mt-3 flex min-h-0 flex-1 flex-row overflow-hidden rounded-lg border border-neutral-mid-gray bg-white">
          <div className="flex min-h-0 w-[272px] flex-none flex-col border-r border-neutral-gray">
            <ClientAccessList
              clients={filteredClients}
              pending={filteredPending}
              isLoading={clientsLoading}
              search={search}
              onSearch={(value) => setSearch(value ?? "")}
              selected={selected}
              onSelect={setSelected}
              onRequestAccess={readOnly ? undefined : () => setRequestOpen(true)}
            />
          </div>
          <div className="min-h-0 min-w-0 flex-1 overflow-y-auto p-5">{detailContent}</div>
        </div>
      ) : (
        /* ===== Tablet / Mobile — full-width list of cards; detail opens in an overlay. The
            Request/Search/Pending block stays on top (inside ClientAccessList) at both sizes. ===== */
        <>
          <div className="mt-3 flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-neutral-mid-gray bg-white">
            <ClientAccessList
              layout={isTablet ? "tablet" : "mobile"}
              clients={filteredClients}
              pending={filteredPending}
              isLoading={clientsLoading}
              search={search}
              onSearch={(value) => setSearch(value ?? "")}
              selected={selected}
              onSelect={setSelected}
              // Inline Request button on tablet only; on mobile it becomes the sticky bar below.
              onRequestAccess={readOnly || isMobile ? undefined : () => setRequestOpen(true)}
            />
          </div>

          {/* Mobile — sticky full-width Request button (hidden in the operator's read-only view). */}
          {isMobile && !readOnly && (
            <div className="sticky bottom-0 z-10 -mx-5 border-t border-neutral-gray bg-white px-5 py-3">
              <Button variant="primary" size="lg" startIcon="Add" className="w-full" onClick={() => setRequestOpen(true)}>
                Request Client Access
              </Button>
            </div>
          )}
        </>
      )}

      <Modal open={isTablet && !!selected} onClose={() => setSelected(null)} variant="web-medium" className="max-h-[90vh] overflow-y-auto">
        {renderOverlayDetail("modal")}
      </Modal>

      {/* Drawer's first child is the (unused) controlled trigger. */}
      <Drawer open={isMobile && !!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <span className="hidden" aria-hidden />
        <div className="pb-2">{renderOverlayDetail("drawer")}</div>
      </Drawer>

      {/* Both mutation modals are unavailable in the operator's read-only view — the triggering
          buttons are hidden and the modals are never mounted (they target the logged-in EA's own
          access, not the managed EA's). */}
      {!readOnly && (
        <>
          <RequestAccessModal open={requestOpen} onClose={() => setRequestOpen(false)} />
          <RemoveMyAccessModal
            link={selectedClient}
            open={removeOpen}
            onClose={() => setRemoveOpen(false)}
            onRemoved={() => setSelected(null)}
          />
        </>
      )}
    </div>
  );
};
